import React, {memo} from "react";
import {Link} from "@inertiajs/react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import Farbfeld from "./Farbfeld.jsx";
import AuslaufendAbzeichen from "./AuslaufendAbzeichen.jsx";
import {ZellMitTooltip, ZellTooltipProvider} from "./ZellTooltip.jsx";
import KwZeitleiste from "./KwZeitleiste.jsx";
import TabellenUeberlaufSchatten, {useTabellenUeberlauf} from "./TabellenUeberlaufSchatten.jsx";
import {AMPEL_FARBEN, AMPEL_LABEL} from "./AmpelSelect.jsx";
import {formatFarbnummer, formatKw, formatMenge, formatProzent, heutigeKw, kwZuMontag, markeLabel, produktionsstatusLabel} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const PRODUKTIONSSTATUS_BADGE = {
    OK: "text-bg-success",
    "PRODUCTION NOT PLANNED": "text-bg-danger",
    "PARTIALLY PRODUCED": "text-bg-warning",
};

const PRODUKTIONSSTATUS_HILFE = {
    OK: "Produktion ist abgeschlossen bzw. planmäßig gedeckt.",
    "PRODUCTION NOT PLANNED": "Für diesen Farbton ist noch keine Produktion eingeplant.",
    "PARTIALLY PRODUCED": "Die Produktion deckt bisher nur einen Teil der Menge.",
};

// Feste vordere Spalten (Auftrag AP7 B) - Breiten in px, sticky beim
// waagerechten Scrollen der Wochenspalten (Vorbild LieferfaehigkeitTabelle.jsx).
// table-layout: fixed (siehe unten) sorgt dafür, dass die Spalten wirklich so
// breit sind wie hier angegeben - sonst verschieben sich die "left"-Offsets
// gegen die tatsächlich gerenderte Breite und Inhalte überlappen sich beim
// Scrollen (identischer Fehler wie LieferfaehigkeitTabelle.jsx, Befund 10).
const FIXE_SPALTEN = [
    {key: "ident", label: "Farbton", width: 215, hilfe: "Farbnummer und Bezeichnung des Farbtons."},
    {key: "artikelnummer", label: "Artikelnr.", width: 95, hilfe: "Interne Artikelnummer, verlinkt zum Artikel-Blatt."},
    {key: "produktionsstatus", label: "Produktion", width: 140, hilfe: "Stand der Produktion aus der Ampel-Datei."},
    {key: "offen", label: "Rest bei Cosmos", width: 110, hilfe: "Menge aus dem Produktionsstand, die weder zugesagt noch produziert ist – nicht zu verwechseln mit der offenen Kundenmenge."},
];

function spaltenLinks() {
    let summe = 0;
    return FIXE_SPALTEN.map((spalte) => {
        const links = summe;
        summe += spalte.width;
        return links;
    });
}

const LINKS = spaltenLinks();
const WOCHE_BREITE = 96;

function IdentitaetName({farbnummer, farbnummerUnter, farbname, ausserhalbSortiment, auslaufend}) {
    const nummer = farbnummer !== null && farbnummer !== undefined ? `${formatFarbnummer(farbnummer, farbnummerUnter)} ` : "";
    const text = `${nummer}${farbname || (ausserhalbSortiment ? "außerhalb des Sortiments" : "–")}`;

    // Befund N6 (Rückmeldung 22.09.2026, wie LieferfaehigkeitTabelle.jsx):
    // Das Abzeichen auslaufender Farbtöne steht nicht im Textfluss, sondern
    // fest oben rechts in der Zelle (Rückmeldung Thorsten 23.09.2026: die
    // Position darf nicht springen) - siehe LieferplanZeile.
    // Frühere Fassung: Name und Abzeichen in EINEM
    // umbrechenden Kasten statt als getrennte Flex-Geschwister - sonst
    // rutscht das Abzeichen bei langen Namen als eigenes Element aus der
    // Spalte.
    return (
        <span className="d-inline-flex align-items-center gap-1">
            <ZellMitTooltip text={text} className="text-truncate d-inline-block" style={{maxWidth: ausserhalbSortiment ? 100 : 155}}>
                {text}
            </ZellMitTooltip>
            {/* N9: Zeilen außerhalb des Sortiments tragen ein eigenes Abzeichen
                und stehen am Ende der Tabelle (Sortierung siehe LieferplanTabelle). */}
            {ausserhalbSortiment && (
                <ZellMitTooltip text="Dieser Artikel ist in Oskars Excel enthalten, gehört aber zu keinem gepflegten Farbton in unserem Sortiment.">
                    <span className="badge text-bg-secondary">außerhalb Sortiment</span>
                </ZellMitTooltip>
            )}
        </span>
    );
}

/**
 * Baut den Tooltip-Text einer Zelle aus Wunsch/Zusage/Ist/Vorschlag und der
 * Ampel-Herkunft. Bei Rot zusätzlich die Lesart aus "rot_deutung"
 * (App\Services\Cosmos\LieferplanQuery::deuteRotAmpel(), Präzisierung des
 * Koordinators 19.09.2026: Rot MIT Menge = verschoben, Rot OHNE Menge =
 * kommt nicht).
 */
function zellTooltipText(zelle) {
    if (!zelle) {
        return null;
    }
    const ampelLabel = zelle.ampel ? AMPEL_LABEL[zelle.ampel] || zelle.ampel : "ohne Farbe";
    const herkunft = zelle.ampel_errechnet ? "errechnet" : "gespeichert (Oskars Excel)";
    const basis = `Wunsch ${formatMenge(zelle.menge_wunsch)} · Zusage ${formatMenge(zelle.menge_zusage)} · Ist ${formatMenge(zelle.menge_ist)} · Neuer Vorschlag ${formatMenge(zelle.menge_vorschlag_neu)} · Ampel ${ampelLabel} (${herkunft})`;
    return zelle.rot_deutung ? `${basis} · ${zelle.rot_deutung}` : basis;
}

function zellFarbe(zelle) {
    if (!zelle || !zelle.ampel) {
        return {};
    }
    return {
        backgroundColor: AMPEL_FARBEN[zelle.ampel] || "#fff",
        border: zelle.ampel_errechnet ? "1px dashed #868e96" : "1px solid rgba(0,0,0,.12)",
    };
}

function zellInhalt(zelle, ansicht) {
    if (!zelle) {
        return null;
    }
    switch (ansicht) {
        case "wunsch":
            return formatMenge(zelle.menge_wunsch);
        case "zusage":
            return formatMenge(zelle.menge_zusage);
        case "geliefert":
            return formatMenge(zelle.menge_ist);
        case "vorschlag_neu":
            return zelle.menge_vorschlag_neu ? formatMenge(zelle.menge_vorschlag_neu) : "–";
        case "wunsch_zusage":
        default:
            return (
                <div className="lh-1">
                    <div>{formatMenge(zelle.menge_wunsch)}</div>
                    <div className="small text-muted">{formatMenge(zelle.menge_zusage)}</div>
                </div>
            );
    }
}

const LieferplanZeile = memo(function LieferplanZeile({zeile, spalten, ansicht, canArtikel, onZelleKlick}) {
    const produktionsstand = zeile.produktionsstand;
    const auslaufend = Boolean(zeile.auslaufend) && !zeile.ausserhalb_sortiment;
    // Auslaufende Farbtöne heben sich über die ganze Zeile ab, ohne zu warnen
    // (Rückmeldung Thorsten 23.09.2026).
    const zeilenHintergrund = auslaufend ? "#e2e6ea" : "#fff";

    return (
        <tr style={auslaufend ? {backgroundColor: zeilenHintergrund} : undefined} className={auslaufend ? "text-muted" : undefined}>
            <td style={{position: "sticky", left: LINKS[0], zIndex: 2, background: zeilenHintergrund, width: FIXE_SPALTEN[0].width, overflow: "hidden"}}>
                <div className="d-flex align-items-center gap-2">
                    <Farbfeld farbwert={zeile.farbwert} />
                    <IdentitaetName
                        farbnummer={zeile.farbnummer}
                        farbnummerUnter={zeile.farbnummer_unter}
                        farbname={zeile.farbname}
                        ausserhalbSortiment={zeile.ausserhalb_sortiment}
                        auslaufend={zeile.auslaufend}
                    />
                </div>
                {auslaufend && (
                    <span style={{position: "absolute", top: 2, right: 4, zIndex: 1}}>
                        <AuslaufendAbzeichen />
                    </span>
                )}
            </td>
            <td style={{position: "sticky", left: LINKS[1], zIndex: 2, background: zeilenHintergrund, width: FIXE_SPALTEN[1].width, overflow: "hidden"}}>
                {canArtikel ? (
                    <Link href={`/cosmos/artikel/${zeile.artikel_nummer}`} className="small">
                        {zeile.artikel_nummer}
                    </Link>
                ) : (
                    <span className="small">{zeile.artikel_nummer}</span>
                )}
            </td>
            <td style={{position: "sticky", left: LINKS[2], zIndex: 2, background: "#fff", width: FIXE_SPALTEN[2].width, overflow: "hidden"}}>
                {produktionsstand ? (
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG}
                        placement="top"
                        overlay={
                            <Tooltip id={`prod-${zeile.artikel_nummer}`}>
                                {PRODUKTIONSSTATUS_HILFE[produktionsstand.status] || produktionsstand.status_text || produktionsstand.status}
                                {produktionsstand.stand_am ? ` (Stand ${produktionsstand.stand_am.split("-").reverse().join(".")})` : ""}
                            </Tooltip>
                        }
                    >
                        <span
                            className={`badge ${PRODUKTIONSSTATUS_BADGE[produktionsstand.status] || "text-bg-secondary"}`}
                            style={{whiteSpace: "normal", wordBreak: "break-word", display: "inline-block", textAlign: "left"}}
                            tabIndex={0}
                        >
                            {produktionsstatusLabel(produktionsstand.status, produktionsstand.status_text)}
                        </span>
                    </OverlayTrigger>
                ) : (
                    <ZellMitTooltip text="Für diesen Farbton liegt kein Produktionsstand vor." className="text-muted small">
                        –
                    </ZellMitTooltip>
                )}
            </td>
            <td style={{position: "sticky", left: LINKS[3], zIndex: 2, background: "#fff", width: FIXE_SPALTEN[3].width, overflow: "hidden"}}>
                {produktionsstand?.menge_offen_rest ? (
                    formatMenge(produktionsstand.menge_offen_rest)
                ) : (
                    <ZellMitTooltip text="Kein Rest offen – alles ist entweder zugesagt oder bereits produziert." className="text-muted small">
                        –
                    </ZellMitTooltip>
                )}
            </td>
            {spalten.map((spalte) => {
                const kwSchluessel = spalte.tbd ? "TBD" : spalte.kw;
                const zelle = zeile.wochen[kwSchluessel];
                return (
                    <td key={kwSchluessel} style={{width: WOCHE_BREITE, textAlign: "center", overflow: "hidden", cursor: zelle ? "pointer" : "default", ...zellFarbe(zelle)}}>
                        {zelle ? (
                            <ZellMitTooltip text={zellTooltipText(zelle)} className="d-inline-block w-100" style={{padding: "2px 4px"}}>
                                <button
                                    type="button"
                                    className="btn btn-link p-0 border-0 text-decoration-none text-reset w-100"
                                    onClick={() => onZelleKlick(zeile.artikel_nummer, kwSchluessel, zelle)}
                                >
                                    {zellInhalt(zelle, ansicht)}
                                </button>
                            </ZellMitTooltip>
                        ) : (
                            ""
                        )}
                    </td>
                );
            })}
        </tr>
    );
});

function WochenKopf({spalte, canLieferungen, istAktuell = false}) {
    const label = spalte.tbd ? "Woche offen (TBD)" : formatKw(spalte.kw);
    const montag = spalte.montag ? spalte.montag.split("-").reverse().join(".") : null;

    return (
        <th
            style={{
                position: "sticky",
                top: 44,
                zIndex: 3,
                background: istAktuell ? "#e7f1ff" : "#fff",
                overflow: "hidden",
                whiteSpace: "normal",
                wordBreak: "normal",
                overflowWrap: "normal",
            }}
            className="align-top"
        >
            <div className={`fw-semibold text-nowrap ${istAktuell ? "text-primary" : ""}`}>{label}</div>
            {montag && <div className="small text-muted">ab {montag}</div>}
            <div className="d-flex flex-column gap-1 mt-1">
                {spalte.lieferungen.map((l) => (
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG}
                        key={l.id}
                        placement="top"
                        overlay={
            <Tooltip id={`lkw-${l.id}`}>
                                {markeLabel(l.marke)} nach {l.zielort || "unbekanntes Ziel"} · Basis {l.basis_label || (l.basis_feld === "zusage" ? "Cosmos-Vorschlag" : "Bestellwunsch")}{" "}
                                {formatMenge(l.basis)} von {formatMenge(l.kapazitaet)} Stück ({formatProzent(l.auslastung_prozent)}) · Rest{" "}
                                {formatMenge(l.rest_dosen)} Dosen ({l.rest_paletten ?? "–"} Paletten)
                            </Tooltip>
                        }
                    >
                        <div>
                            {canLieferungen ? (
                                <Link href={`/cosmos/lieferungen/${l.id}`} className="small d-block text-truncate" style={{maxWidth: WOCHE_BREITE - 10}}>
                                    {markeLabel(l.marke)}
                                </Link>
                            ) : (
                                <span className="small d-block text-truncate" style={{maxWidth: WOCHE_BREITE - 10}}>
                                    {markeLabel(l.marke)}
                                </span>
                            )}
                            <div className="progress" style={{height: 5}}>
                                <div
                                    className={`progress-bar ${l.auslastung_prozent > 100 ? "bg-danger" : l.auslastung_prozent >= 60 ? "bg-success" : "bg-warning"}`}
                                    style={{width: `${Math.min(100, l.auslastung_prozent)}%`}}
                                />
                            </div>
                            <div className={`small ${l.auslastung_prozent > 100 ? "text-danger fw-semibold" : "text-muted"}`}>
                                {l.auslastung_prozent > 100 ? `überladen: ${formatProzent(l.auslastung_prozent)}` : formatProzent(l.auslastung_prozent)}
                            </div>
                        </div>
                    </OverlayTrigger>
                ))}
            </div>
        </th>
    );
}

/**
 * Der Lieferplan-Kalender: eine Zeile je Farbton, Kalenderwochen als Spalten
 * (AP7 B, PLAN.md Abschnitt 5 "Lieferplan"). Fixierte vordere Spalten,
 * gemeinsamer Tooltip-Baustein (ZellTooltip.jsx) für Performance bei vielen
 * Zellen (Vorbild LieferfaehigkeitTabelle.jsx).
 */
export default function LieferplanTabelle({zeilen, wochen, ansicht, canArtikel, canLieferungen, onZelleKlick, tabellenRef}) {
    if (!zeilen || zeilen.length === 0) {
        return <p className="text-muted">Keine Farbtöne treffen die aktuelle Filterung.</p>;
    }

    const spalten = wochen.map((w) => ({kw: w.kw, tbd: w.tbd, montag: w.montag, lieferungen: w.lieferungen}));
    const gesamtBreiteFix = FIXE_SPALTEN.reduce((summe, s) => summe + s.width, 0);

    // N9: Zeilen außerhalb des Sortiments stehen am Ende statt verstreut
    // zwischen den echten Farbtönen (stabile Sortierung - die Reihenfolge
    // innerhalb der Gruppen bleibt, wie vom Server geliefert). Auslaufende
    // Farbtöne (Freigabe Thorsten 22.09.2026) stehen ebenfalls ganz unten,
    // aber noch vor "außerhalb Sortiment" - sie gehören zumindest noch zum
    // gepflegten Sortiment.
    const zeilenSortiert = [...zeilen].sort((a, b) => {
        const ausA = a.ausserhalb_sortiment ? 2 : (a.auslaufend ? 1 : 0);
        const ausB = b.ausserhalb_sortiment ? 2 : (b.auslaufend ? 1 : 0);
        return ausA - ausB;
    });

    const heutigeWoche = heutigeKw();
    const zeitleisteSpalten = spalten.map((spalte) => ({
        schluessel: spalte.tbd ? "TBD" : spalte.kw,
        montag: spalte.tbd || !spalte.montag ? null : kwZuMontag(spalte.kw),
    }));

    // Befund N2 (Rückmeldung 22.09.2026): bei 1440 px wirkte die letzte
    // Spalte ("Woche offen") abgeschnitten statt scrollbar - der Schatten
    // macht das waagerechte Scrollen erkennbar, sobald es tatsächlich mehr
    // Spalten gibt als sichtbar sind.
    const ueberlauf = useTabellenUeberlauf(tabellenRef, [spalten.length]);

    return (
        <ZellTooltipProvider>
            <div className="position-relative">
                <div className="table-responsive border rounded-1" ref={tabellenRef} style={{maxHeight: "70vh"}}>
                    <table
                        className="table table-sm mb-0"
                        style={{minWidth: gesamtBreiteFix + spalten.length * WOCHE_BREITE, tableLayout: "fixed"}}
                    >
                        <colgroup>
                            {FIXE_SPALTEN.map((spalte) => (
                                <col key={spalte.key} style={{width: spalte.width}} />
                            ))}
                            {spalten.map((spalte) => (
                                <col key={spalte.tbd ? "TBD" : spalte.kw} style={{width: WOCHE_BREITE}} />
                            ))}
                        </colgroup>
                        <thead>
                            <KwZeitleiste spalten={zeitleisteSpalten} vorspaltenAnzahl={FIXE_SPALTEN.length} vorspaltenBreite={gesamtBreiteFix} topJahr={0} topQuartal={22} />
                            <tr>
                                {FIXE_SPALTEN.map((spalte, index) => (
                                    <th
                                        key={spalte.key}
                                        style={{
                                            position: "sticky",
                                            left: LINKS[index],
                                            top: 44,
                                            zIndex: 4,
                                            background: "#fff",
                                            overflow: "hidden",
                                            whiteSpace: "normal",
                                            wordBreak: "normal",
                                            overflowWrap: "normal",
                                            verticalAlign: "bottom",
                                        }}
                                    >
                                        <HeaderWithHelp help={spalte.hilfe} placement="top">
                                            {spalte.label}
                                        </HeaderWithHelp>
                                    </th>
                                ))}
                                {spalten.map((spalte) => (
                                    <WochenKopf
                                        key={spalte.tbd ? "TBD" : spalte.kw}
                                        spalte={spalte}
                                        canLieferungen={canLieferungen}
                                        istAktuell={!spalte.tbd && spalte.kw === heutigeWoche}
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {zeilenSortiert.map((zeile) => (
                                <LieferplanZeile
                                    key={zeile.artikel_nummer}
                                    zeile={zeile}
                                    spalten={spalten}
                                    ansicht={ansicht}
                                    canArtikel={canArtikel}
                                    onZelleKlick={onZelleKlick}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
                <TabellenUeberlaufSchatten sichtbar={ueberlauf} />
            </div>
        </ZellTooltipProvider>
    );
}
